import React from "react";

// One source of truth for the note/coin ladder. `qty` is the column name the
// database (and therefore show/edit/update) uses; `createQty` is the spelling
// the store() endpoint still expects for a new record.
export const DENOMINATIONS = [
  { value: 1000, label: "1,000", qty: "thousand", createQty: "thousand", total: "thousand_total" },
  { value: 500, label: "500", qty: "five_hundred", createQty: "five_hundreed", total: "five_hundred_total" },
  { value: 200, label: "200", qty: "two_hundred", createQty: "two_hundreed", total: "two_hundred_total" },
  { value: 100, label: "100", qty: "one_hundred", createQty: "hundreed", total: "one_hundred_total" },
  { value: 50, label: "50", qty: "fifty", createQty: "fifty", total: "fifty_total" },
  { value: 20, label: "20", qty: "twenty", createQty: "tweenty", total: "twenty_total" },
  { value: 10, label: "10", qty: "ten", createQty: "ten", total: "ten_total" },
  { value: 5, label: "5", qty: "five", createQty: "five", total: "five_total" },
  { value: 2, label: "2", qty: "two", createQty: "two", total: "two_total" },
  { value: 1, label: "1", qty: "one", createQty: "one", total: "one_total" },
];

export const num = (value) => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

export const money = (value) =>
  num(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const todayInput = () => {
  const now = new Date();
  const month = ("0" + (now.getMonth() + 1)).slice(-2);
  const day = ("0" + now.getDate()).slice(-2);
  return `${now.getFullYear()}-${month}-${day}`;
};

// Gross counted cash from a map of quantities keyed by the canonical `qty` name.
export const grossOf = (counts) =>
  DENOMINATIONS.reduce((sum, d) => sum + num(counts[d.qty]) * d.value, 0);

// Shared look for every Cash Count screen, in the same house style the
// journal voucher screens use (navy #2a2962 headings, red required marks).
export const CashCountStyles = () => (
  <style>
    {`
      .cc-panel {
        background: #fff;
        border: 1px solid #e6e8f0;
        border-radius: 10px;
        box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
        margin-bottom: 20px;
      }
      .cc-panel-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 14px 20px;
        border-bottom: 1px solid #e6e8f0;
        font-size: 0.95rem;
        font-weight: 600;
        color: #2a2962;
      }
      .cc-panel-head .cc-head-note {
        font-size: 0.75rem;
        font-weight: 500;
        color: #8b90a5;
        text-transform: none;
        letter-spacing: 0;
      }
      .cc-panel-body { padding: 20px; }
      .cc-panel label {
        display: block;
        margin-bottom: 6px;
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #6b7280;
      }
      .cc-panel .form-control:focus {
        border-color: #2a2962;
        box-shadow: 0 0 0 0.2rem rgba(42, 41, 98, 0.12);
      }
      .cc-panel .required { color: #e42320; font-size: 0.5rem; vertical-align: super; }

      .cc-num {
        text-align: right;
        font-variant-numeric: tabular-nums;
        font-feature-settings: "tnum";
      }

      /* Stat strip */
      .cc-stat {
        position: relative;
        background: #fff;
        border: 1px solid #e6e8f0;
        border-left: 3px solid #2a2962;
        border-radius: 8px;
        padding: 14px 16px;
        height: 100%;
        box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
      }
      .cc-stat.is-alert { border-left-color: #e42320; }
      .cc-stat-label {
        display: block;
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #8b90a5;
        margin-bottom: 4px;
      }
      .cc-stat-value {
        font-size: 1.25rem;
        font-weight: 700;
        color: #2a2962;
        line-height: 1.2;
        font-variant-numeric: tabular-nums;
        font-feature-settings: "tnum";
      }
      .cc-stat.is-alert .cc-stat-value { color: #e42320; }
      .cc-stat-icon {
        position: absolute;
        top: 14px;
        right: 16px;
        font-size: 1.1rem;
        color: #e6e8f0;
      }

      /* Denomination ladder */
      .cc-count-table { width: 100%; margin-bottom: 0; }
      .cc-count-table thead th {
        padding: 10px 12px;
        background: #2a2962;
        color: #fff;
        font-size: 0.72rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        border: 0;
        white-space: nowrap;
      }
      .cc-count-table tbody td {
        padding: 8px 12px;
        border-bottom: 1px solid #f0f1f6;
        vertical-align: middle;
      }
      .cc-count-table tbody tr:hover { background: #fafbfd; }
      .cc-count-table tfoot td, .cc-count-table tfoot th {
        padding: 11px 12px;
        background: #f2f3f9;
        font-size: 0.9rem;
        color: #2a2962;
        border: 0;
      }
      .cc-count-table tfoot tr.cc-net td, .cc-count-table tfoot tr.cc-net th {
        border-top: 2px solid #2a2962;
        font-weight: 700;
        font-size: 1rem;
      }
      .cc-note {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 62px;
        padding: 4px 10px;
        border: 1px solid #d7dae8;
        border-radius: 4px;
        background: #f7f8fc;
        font-size: 0.82rem;
        font-weight: 600;
        color: #2a2962;
        font-variant-numeric: tabular-nums;
      }
      .cc-op { color: #b6bacb; font-size: 0.8rem; }
      .cc-qty-input { max-width: 140px; text-align: right; }
      .cc-line-total { font-size: 0.9rem; font-weight: 600; color: #35374d; }
      .cc-line-total.is-zero { color: #b6bacb; font-weight: 500; }

      /* Read-only key/value rows */
      .cc-kv {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 16px;
        padding: 9px 0;
        border-bottom: 1px solid #f0f1f6;
      }
      .cc-kv:last-child { border-bottom: 0; }
      .cc-kv-label {
        font-size: 0.72rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #8b90a5;
      }
      .cc-kv-value { font-size: 0.9rem; font-weight: 600; color: #35374d; }

      .cc-total-box {
        border: 1px solid #e6e8f0;
        border-radius: 8px;
        overflow: hidden;
      }
      .cc-total-row {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        padding: 10px 16px;
        border-bottom: 1px solid #f0f1f6;
        font-size: 0.85rem;
        color: #6b7280;
      }
      .cc-total-row span:last-child {
        font-weight: 600;
        color: #35374d;
        font-variant-numeric: tabular-nums;
      }
      .cc-total-row.cc-total-net {
        border-bottom: 0;
        background: #2a2962;
        color: #fff;
        padding: 14px 16px;
      }
      .cc-total-row.cc-total-net span { color: #fff; }
      .cc-total-row.cc-total-net span:last-child {
        color: #fff;
        font-size: 1.15rem;
        font-weight: 700;
      }

      .cc-empty {
        padding: 48px 20px;
        text-align: center;
        color: #8b90a5;
      }
      .cc-empty i { font-size: 2rem; color: #d7dae8; display: block; margin-bottom: 10px; }

      .cc-pill {
        display: inline-block;
        padding: 3px 10px;
        border-radius: 12px;
        font-size: 0.75rem;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
      }
      .cc-pill-value { background: #eef0f8; color: #2a2962; }
      .cc-pill-muted { background: #f5f6fa; color: #b6bacb; }
      .cc-pill-alert { background: #fdecec; color: #e42320; }

      @media print {
        .cc-no-print { display: none !important; }
        .cc-panel { box-shadow: none; break-inside: avoid; }
      }
    `}
  </style>
);
